import { useEffect, useState, useCallback } from "react";
import { IconButton } from "@/components/IconButton";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/layouts/AppShell";
import { eventSource } from "@/api/client";
import {
  getPlaybackSession,
  getPlaybackSessions,
  reportPlaybackEvent,
} from "@/api/endpoints";
import type { Item, PlaybackSession } from "@/types/api";

const ACTIVE_SESSION_STALE_MS = 30_000;
const NOW_PLAYING_SESSION_PAGE_LIMIT = 20;

/**
 * Persistent now-playing bar at the bottom of the app (Jellyfin pattern).
 * Shows when a playback session is active and follows playback SSE events.
 */
export function NowPlayingBar() {
  const navigate = useNavigate();
  const [activeSession, setActiveSession] = useState<ActiveNowPlayingSession | null>(null);
  const [pendingAction, setPendingAction] = useState<"toggle" | "stop" | null>(null);

  const hydrateSession = useCallback(async (sessionId: string) => {
    const response = await getPlaybackSession(sessionId, "item");
    setActiveSession(toNowPlayingSession(response.data));
  }, []);

  const reloadLatestSession = useCallback(async () => {
    const response = await getPlaybackSessions({ limit: NOW_PLAYING_SESSION_PAGE_LIMIT });
    const next = response.data.find(isDisplayableNowPlayingSession);
    if (!next) {
      setActiveSession(null);
      return;
    }
    await hydrateSession(next.id);
  }, [hydrateSession]);

  useEffect(() => {
    reloadLatestSession().catch(() => {});
    const refreshTimer = window.setInterval(() => {
      reloadLatestSession().catch(() => {});
    }, 15_000);

    const stream = eventSource();
    const handleLifecycleEvent = (evt: Event) => {
      const message = evt as MessageEvent<string>;
      const payload = parsePlaybackEvent(message.data);
      if (!payload) return;

      if (payload.eventType === "playback.stopped") {
        setActiveSession((current) => {
          if (!current || current.sessionId !== payload.sessionId) {
            return current;
          }
          return null;
        });
        reloadLatestSession().catch(() => {});
        return;
      }

      setActiveSession((current) => mergePlaybackEvent(current, payload));
      hydrateSession(payload.sessionId).catch(() => {});
    };

    stream.addEventListener("playback.started", handleLifecycleEvent);
    stream.addEventListener("playback.updated", handleLifecycleEvent);
    stream.addEventListener("playback.progress", handleLifecycleEvent);
    stream.addEventListener("playback.stopped", handleLifecycleEvent);
    stream.onerror = () => {
      stream.close();
    };

    return () => {
      window.clearInterval(refreshTimer);
      stream.removeEventListener("playback.started", handleLifecycleEvent);
      stream.removeEventListener("playback.updated", handleLifecycleEvent);
      stream.removeEventListener("playback.progress", handleLifecycleEvent);
      stream.removeEventListener("playback.stopped", handleLifecycleEvent);
      stream.close();
    };
  }, [hydrateSession, reloadLatestSession]);

  if (!activeSession) return null;
  const session = activeSession;

  const progress = session.durationMS > 0
    ? (session.positionMS / session.durationMS) * 100
    : 0;
  const stateLabel = session.state === "paused" ? "Paused" : "Playing";

  async function handleTogglePlayback(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (pendingAction) return;

    const nextEvent = session.state === "paused" ? "resume" : "pause";
    const nextState = session.state === "paused" ? "active" : "paused";

    setPendingAction("toggle");
    setActiveSession((current) => current ? { ...current, state: nextState } : current);
    try {
      await reportPlaybackEvent(session.sessionId, {
        event: nextEvent,
        position_ms: session.positionMS,
      });
      await hydrateSession(session.sessionId);
    } catch {
      await reloadLatestSession().catch(() => {});
    } finally {
      setPendingAction(null);
    }
  }

  async function handleStopPlayback(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (pendingAction) return;

    setPendingAction("stop");
    try {
      await reportPlaybackEvent(session.sessionId, {
        event: "stop",
        position_ms: session.positionMS,
      });
      setActiveSession(null);
      await reloadLatestSession().catch(() => {});
    } catch {
      await reloadLatestSession().catch(() => {});
    } finally {
      setPendingAction(null);
    }
  }

  return (
    <div className="now-playing-bar" onClick={() => navigate(`/play/${session.sessionId}`)}>
      <div className="now-playing-progress">
        <div className="now-playing-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="now-playing-content">
        <div className="now-playing-meta">
          <span className="now-playing-title">{session.title}</span>
          <span className="now-playing-status">
            {stateLabel}
            {session.conservativeStreaming ? " • Slow link" : ""}
          </span>
        </div>
        <div className="now-playing-controls">
          <IconButton
            className="now-playing-btn"
            onClick={handleTogglePlayback}
            disabled={pendingAction !== null}
            label={session.state === "paused" ? "Resume playback" : "Pause playback"}
          >
            <Icon name={session.state === "paused" ? "play" : "pause"} />
          </IconButton>
          <IconButton
            className="now-playing-btn"
            align="end"
            onClick={handleStopPlayback}
            disabled={pendingAction !== null}
            label="Stop playback"
          >
            <Icon name="stop" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}

interface ActiveNowPlayingSession {
  sessionId: string;
  itemId: string;
  title: string;
  positionMS: number;
  durationMS: number;
  state: PlaybackSession["attributes"]["state"];
  conservativeStreaming: boolean;
}

interface PlaybackEventPayload {
  eventType: "playback.started" | "playback.updated" | "playback.progress" | "playback.stopped";
  sessionId: string;
  itemId: string;
  state: PlaybackSession["attributes"]["state"];
  positionMS: number;
  conservativeStreaming: boolean;
}

function toNowPlayingSession(session: PlaybackSession): ActiveNowPlayingSession {
  const item = session.relationships.item.data;
  const embeddedItem = isEmbeddedItem(item) ? item : null;

  return {
    sessionId: session.id,
    itemId: embeddedItem?.id ?? item.id,
    title: embeddedItem?.attributes.title ?? item.id,
    positionMS: session.attributes.position_ms,
    durationMS: embeddedItem?.attributes.duration_ms ?? 0,
    state: session.attributes.state,
    conservativeStreaming: session.attributes.conservative_streaming,
  };
}

function parsePlaybackEvent(raw: string): PlaybackEventPayload | null {
  const payload = JSON.parse(raw) as {
    session_id?: string;
    item_id?: string;
    state?: PlaybackSession["attributes"]["state"];
    position_ms?: number;
    conservative_streaming?: boolean;
    trigger?: string;
  };

  if (!payload.session_id || !payload.item_id || !payload.state) {
    return null;
  }

  const trigger = payload.trigger;
  let eventType: PlaybackEventPayload["eventType"] = "playback.updated";
  if (trigger === "session_created") eventType = "playback.started";
  else if (trigger === "progress") eventType = "playback.progress";
  else if (trigger === "stop" || trigger === "session_deleted") eventType = "playback.stopped";

  return {
    eventType,
    sessionId: payload.session_id,
    itemId: payload.item_id,
    state: payload.state,
    positionMS: payload.position_ms ?? 0,
    conservativeStreaming: payload.conservative_streaming ?? false,
  };
}

function mergePlaybackEvent(
  current: ActiveNowPlayingSession | null,
  payload: PlaybackEventPayload,
): ActiveNowPlayingSession {
  if (!current || current.sessionId !== payload.sessionId) {
    return {
      sessionId: payload.sessionId,
      itemId: payload.itemId,
      title: payload.itemId,
      positionMS: payload.positionMS,
      durationMS: 0,
      state: payload.state,
      conservativeStreaming: payload.conservativeStreaming,
    };
  }

  return {
    ...current,
    positionMS: payload.positionMS,
    state: payload.state,
    conservativeStreaming: payload.conservativeStreaming,
  };
}

function isEmbeddedItem(item: PlaybackSession["relationships"]["item"]["data"]): item is Item {
  return "attributes" in item;
}

function isDisplayableNowPlayingSession(session: PlaybackSession): boolean {
  if (session.attributes.state === "stopped") {
    return false;
  }

  if (session.attributes.state !== "active") {
    return true;
  }

  const updatedAt = Date.parse(session.attributes.updated_at);
  if (Number.isNaN(updatedAt)) {
    return false;
  }

  return Date.now() - updatedAt <= ACTIVE_SESSION_STALE_MS;
}
